import { getBrowserTabsAttachments } from "@mirage/mosaics/Chat/components/chat/BrowserTabs/BrowserTabsIngestion";
import type {
  ChatSource,
  ChatSourceRecommendation,
} from "@mirage/mosaics/Chat/types";
import type {
  ConversationMessage,
  DocumentInfo,
} from "@mirage/service-conversation/types";
import {
  browserTabChatSourceToDocumentInfo,
  convertChatSourceToDocumentInfo,
} from "@mirage/service-conversation/utils/convertChatSourceToDocumentInfo";
import { type SearchResult } from "@mirage/service-dbx-api";
import { getTabContentFromLocalIndex } from "@mirage/service-extension-tab-ingestion";
import { isNotNull } from "@mirage/shared/util/tiny-utils";
import { useCallback } from "react";

/**
 * Returns the attachments we should pass to the conversations API message payload.
 * This should include attachments based on:
 * 1. any scoped sources (sources that are scoped to a specific set of sources);
 * 2. any additional sources manually added by the user;
 * 3. any relevant transient sources (the current transient source or transient sources in past messages)
 */
export function useGetConversationAttachments(
  scopedSources: ChatSource[],
  manuallyAddedSources: ChatSource[],
  getTransientSource: (() => Promise<DocumentInfo>) | undefined,
  conversationHistory: ConversationMessage[],
  browserContextTabIDs: Set<number> | undefined,
) {
  return useCallback(async () => {
    const allSources = (
      await Promise.all(
        [...scopedSources, ...manuallyAddedSources].map(async (source) => {
          const browserTabSource = getBrowserTabSource(source);
          if (browserTabSource) {
            try {
              const tabContent = await getTabContentFromLocalIndex(
                browserTabSource.uuid,
              );
              if (!tabContent) return null;
              return {
                type: "browser_tab_cache" as const,
                body: tabContent,
                tabInfo: {
                  url: browserTabSource.url ?? undefined,
                  title: browserTabSource.title ?? undefined,
                },
              };
            } catch {
              return null;
            }
          }

          return source;
        }),
      )
    ).filter(isNotNull);

    const allAttachments = allSources.map(convertChatSourceToDocumentInfo);
    if (getTransientSource) {
      allAttachments.push(await getTransientSource());
    }

    // Include all referenced transient sources in the conversation history
    for (const message of conversationHistory) {
      if (message.messageType?.case === "userMessage") {
        for (const attachment of message.messageType.value.attachments) {
          if (attachment.transient) {
            allAttachments.push(attachment);
          }
        }
      }
    }

    if (browserContextTabIDs) {
      const browserTabsAttachments =
        await getBrowserTabsAttachments(browserContextTabIDs);
      const browserTabsDocumentInfos = browserTabsAttachments
        .map(browserTabChatSourceToDocumentInfo)
        .filter((info) => info !== undefined);
      allAttachments.push(...browserTabsDocumentInfos);
    }

    // Deduplicate attachments based on their uuid (first one wins)
    const deduplicatedAttachments: DocumentInfo[] = [];
    const attachmentUUIDs = new Set<string>();
    for (const attachment of allAttachments) {
      if (!attachmentUUIDs.has(attachment.uuid)) {
        deduplicatedAttachments.push(attachment);
        attachmentUUIDs.add(attachment.uuid);
      }
    }
    return deduplicatedAttachments;
  }, [
    scopedSources,
    manuallyAddedSources,
    getTransientSource,
    browserContextTabIDs,
    conversationHistory,
  ]);
}

const getBrowserTabSource = (
  source: ChatSource,
): SearchResult | ChatSourceRecommendation["recommendation"] | null => {
  if (
    source.type === "recommendation" &&
    source.recommendation.recordType[".tag"] === "browser_tab"
  ) {
    return source.recommendation;
  }

  if (
    source.type === "mirage_search_result" &&
    source.searchResult.recordType[".tag"] === "browser_tab"
  ) {
    return source.searchResult;
  }

  return null;
};
